import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Inbox } from 'lucide-react';
import { reviewPartnerApplication } from '../../services/api';
import PartnerProgramLabel from './PartnerProgramLabel';
import {
  APPLICATION_STATUSES,
  TIERS,
  apiErrorCode,
  buildApprovePayload,
  defaultTierForProgram,
  isValidCode,
  normalizeCode,
  normalizeProgram,
} from '../../utils/partnersAdmin';

const APP_STATUS_DEFAULTS = { pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada' };

const REVIEW_ERRORS = {
  CODE_TAKEN: ['partners.applications.errors.codeTaken', 'Ese código ya existe: elegí otro.'],
  PORTAL_USER_EXISTS: [
    'partners.applications.errors.portalUserExists',
    'Ese email ya tiene una cuenta en Resto. Pedile otro email o creá el código a mano.',
  ],
  ALREADY_REVIEWED: [
    'partners.applications.errors.alreadyReviewed',
    'Otra persona ya revisó esta solicitud. Recargá la lista.',
  ],
};

function ApproveModal({ application, onClose, onDone }) {
  const { t } = useTranslation();
  const program = normalizeProgram(application.program);
  const [form, setForm] = useState({
    code: normalizeCode(application.suggestedCode),
    tier: defaultTierForProgram(program),
    partnerName: application.name || '',
    commissionPct: '',
  });
  const [codeError, setCodeError] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidCode(normalizeCode(form.code))) {
      setCodeError(
        t('partners.applications.codeInvalid', 'Entre 4 y 20 letras o números, en mayúsculas')
      );
      return;
    }
    setSaving(true);
    setApiError(null);
    try {
      const result = await reviewPartnerApplication(
        application.applicationId,
        buildApprovePayload(form, program)
      );
      await onDone('approved', result);
    } catch (error) {
      const code = apiErrorCode(error);
      if (error?.response?.status === 409 && REVIEW_ERRORS[code]) {
        const [key, fallback] = REVIEW_ERRORS[code];
        if (code === 'CODE_TAKEN') setCodeError(t(key, fallback));
        else setApiError({ code, message: t(key, fallback) });
      } else {
        console.error('Error approving partner application:', error);
        setApiError({
          code: 'UNKNOWN',
          message: t('partners.applications.errors.generic', 'No se pudo guardar la revisión'),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" role="dialog" aria-modal="true" data-testid="approve-modal">
        <div className="modal-header">
          <h2>
            {t('partners.applications.approveTitle', 'Aprobar a {{name}}', {
              name: application.name,
            })}
          </h2>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label={t('common.close', 'Cerrar')}
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-body">
            <p className="field-hint approve-explainer">
              {t(
                'partners.applications.approveExplainer',
                'Al aprobar se crea el código, se invita al partner a su portal (le llega la contraseña por mail) y se le manda la bienvenida.'
              )}
            </p>
            {apiError && (
              <div
                className="alert alert-error"
                role="alert"
                data-testid="approve-error"
                data-code={apiError.code}
              >
                {apiError.message}
              </div>
            )}
            <div className="form-row">
              <div className={`form-group${codeError ? ' has-error' : ''}`}>
                <label htmlFor="approve-code">{t('partners.code', 'Código')} *</label>
                <input
                  id="approve-code"
                  name="code"
                  type="text"
                  maxLength={20}
                  value={form.code}
                  aria-invalid={!!codeError}
                  onChange={(e) => {
                    setCodeError(null);
                    setForm((prev) => ({ ...prev, code: normalizeCode(e.target.value) }));
                  }}
                />
                {codeError && (
                  <span className="field-error" role="alert" data-testid="approve-code-error">
                    {codeError}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="approve-tier">{t('partners.tier', 'Tier')} *</label>
                <select
                  id="approve-tier"
                  name="tier"
                  value={form.tier}
                  onChange={(e) => setForm((prev) => ({ ...prev, tier: e.target.value }))}
                >
                  {TIERS.map((tier) => (
                    <option key={tier} value={tier}>
                      {tier}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="approve-name">{t('partners.name', 'Nombre')}</label>
                <input
                  id="approve-name"
                  name="partnerName"
                  type="text"
                  value={form.partnerName}
                  onChange={(e) => setForm((prev) => ({ ...prev, partnerName: e.target.value }))}
                />
              </div>
              {program === 'influencer' && (
                <div className="form-group">
                  <label htmlFor="approve-pct">
                    {t('partners.applications.commissionOptional', 'Comisión % (opcional)')}
                  </label>
                  <input
                    id="approve-pct"
                    name="commissionPct"
                    type="number"
                    min="0"
                    placeholder="20"
                    value={form.commissionPct}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, commissionPct: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>
            {program === 'cm' && (
              <p className="field-hint">
                {t(
                  'partners.applications.cmPctHint',
                  'En CM el porcentaje sale de los escalones por locales activos.'
                )}
              </p>
            )}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel', 'Cancelar')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              data-testid="approve-submit"
            >
              {saving
                ? t('common.saving', 'Guardando…')
                : t('partners.applications.approve', 'Aprobar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RejectModal({ application, onClose, onDone }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError(t('partners.applications.reasonRequired', 'Contanos el motivo del rechazo'));
      return;
    }
    setSaving(true);
    try {
      const result = await reviewPartnerApplication(application.applicationId, {
        decision: 'reject',
        rejectionReason: reason.trim(),
      });
      await onDone('rejected', result);
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === 'ALREADY_REVIEWED') {
        setError(t(...REVIEW_ERRORS.ALREADY_REVIEWED));
      } else {
        console.error('Error rejecting partner application:', err);
        setError(t('partners.applications.errors.generic', 'No se pudo guardar la revisión'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" role="dialog" aria-modal="true" data-testid="reject-modal">
        <div className="modal-header">
          <h2>
            {t('partners.applications.rejectTitle', 'Rechazar a {{name}}', {
              name: application.name,
            })}
          </h2>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label={t('common.close', 'Cerrar')}
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-body">
            <div className={`form-group${error ? ' has-error' : ''}`}>
              <label htmlFor="reject-reason">{t('partners.applications.reason', 'Motivo')} *</label>
              <textarea
                id="reject-reason"
                name="rejectionReason"
                rows={3}
                value={reason}
                aria-required="true"
                onChange={(e) => {
                  setError(null);
                  setReason(e.target.value);
                }}
              />
              {error && (
                <span className="field-error" role="alert" data-testid="reject-error">
                  {error}
                </span>
              )}
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel', 'Cancelar')}
            </button>
            <button
              type="submit"
              className="btn btn-danger"
              disabled={saving}
              data-testid="reject-submit"
            >
              {t('partners.applications.reject', 'Rechazar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * RES-94: bandeja de solicitudes para ser partner. Aprobar crea el código e invita al portal;
 * rechazar exige motivo.
 */
function PartnerApplicationsPanel({
  applications,
  loading,
  filter,
  onFilterChange,
  onReviewed,
  applyUrl,
  onCopyApplyUrl,
  fmt,
}) {
  const { t } = useTranslation();
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const handleDone = async (decision, result) => {
    setApproving(null);
    setRejecting(null);
    await onReviewed(decision, result);
  };

  return (
    <div className="partners-applications" data-testid="applications-panel">
      <div className="apply-link-box">
        <span className="detail-label">
          {t('partners.applications.publicLink', 'Link público del formulario')}
        </span>
        <div className="detail-link-row">
          <code data-testid="apply-url">{applyUrl}</code>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCopyApplyUrl}>
            <Copy size={14} /> {t('partners.copyLink', 'Copiar link')}
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <label htmlFor="applications-filter" className="period-label">
          {t('partners.statusLabel', 'Estado')}
        </label>
        <select
          id="applications-filter"
          name="applicationsFilter"
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
        >
          {APPLICATION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`partners.applications.status.${s}`, APP_STATUS_DEFAULTS[s])}
            </option>
          ))}
          <option value="">{t('partners.applications.status.all', 'Todas')}</option>
        </select>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">{t('common.loading', 'Cargando…')}</div>
        ) : applications.length === 0 ? (
          <div className="empty-state" data-testid="applications-empty">
            <Inbox size={48} />
            <p>{t('partners.applications.empty', 'No hay solicitudes en este estado')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="partners-table" data-testid="applications-table">
              <thead>
                <tr>
                  <th>{t('partners.conversionDate', 'Fecha')}</th>
                  <th>{t('partners.name', 'Nombre')}</th>
                  <th>{t('partners.program.label', 'Programa')}</th>
                  <th>{t('partners.applications.contact', 'Contacto')}</th>
                  <th>{t('partners.applications.locations', 'Locales que maneja')}</th>
                  <th>{t('partners.applications.message', 'Mensaje')}</th>
                  <th>{t('partners.statusLabel', 'Estado')}</th>
                  <th>{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((a) => (
                  <tr key={a.applicationId} data-application={a.applicationId}>
                    <td data-label={t('partners.conversionDate', 'Fecha')}>
                      {fmt.date(a.createdAt)}
                    </td>
                    <td data-label={t('partners.name', 'Nombre')}>{a.name}</td>
                    <td data-label={t('partners.program.label', 'Programa')}>
                      <PartnerProgramLabel program={a.program} />
                    </td>
                    <td data-label={t('partners.applications.contact', 'Contacto')}>
                      <span className="contact-lines">
                        {a.email && <a href={`mailto:${a.email}`}>{a.email}</a>}
                        {a.phone && <span>{a.phone}</span>}
                        {a.instagram && <span>@{String(a.instagram).replace(/^@/, '')}</span>}
                      </span>
                    </td>
                    <td data-label={t('partners.applications.locations', 'Locales que maneja')}>
                      {a.locationsManaged ?? '—'}
                      {a.resaleInterest && (
                        <span className="cell-sub">
                          {t('partners.applications.resaleInterest', 'Le interesa revender')}
                        </span>
                      )}
                    </td>
                    <td
                      data-label={t('partners.applications.message', 'Mensaje')}
                      className="application-message"
                    >
                      {a.message || '—'}
                    </td>
                    <td data-label={t('partners.statusLabel', 'Estado')}>
                      <span className={`app-status app-status-${a.status}`}>
                        {t(
                          `partners.applications.status.${a.status}`,
                          APP_STATUS_DEFAULTS[a.status] || a.status
                        )}
                      </span>
                      {a.code && <code className="partner-code cell-sub">{a.code}</code>}
                      {a.rejectionReason && <span className="cell-sub">{a.rejectionReason}</span>}
                    </td>
                    <td data-label={t('common.actions', 'Acciones')} className="partners-actions">
                      {a.status === 'pending' ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            data-testid={`approve-${a.applicationId}`}
                            onClick={() => setApproving(a)}
                          >
                            {t('partners.applications.approve', 'Aprobar')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            data-testid={`reject-${a.applicationId}`}
                            onClick={() => setRejecting(a)}
                          >
                            {t('partners.applications.reject', 'Rechazar')}
                          </button>
                        </>
                      ) : (
                        <span className="cell-sub">{fmt.date(a.reviewedAt)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {approving && (
        <ApproveModal
          application={approving}
          onClose={() => setApproving(null)}
          onDone={handleDone}
        />
      )}
      {rejecting && (
        <RejectModal
          application={rejecting}
          onClose={() => setRejecting(null)}
          onDone={handleDone}
        />
      )}
    </div>
  );
}

export default PartnerApplicationsPanel;
